import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { competitionAPI, predictionAPI, matchAPI } from '../../api/endpoints';
import { formatXPUNT, formatTimeAgo, formatDate, getUserId } from '../../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaTrophy, FaClock, FaLock, FaUnlock,
  FaCheck, FaTimes, FaPlus, FaCalendar, FaDollarSign,
  FaChartBar, FaCopy, FaShare, FaFlag, FaEdit,
  FaTrash, FaSave, FaSpinner, FaInfoCircle,
  FaFutbol, FaSearch, FaMapPin, FaGlobe,
  FaBullseye, FaCalculator
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import PredictionForm from './PredictionForm';
import './CompetitionDetail.css';

// ==================== DEFAULT OUTCOMES ====================
const DEFAULT_OUTCOMES = [
  { name: 'Home Win', points: 10, type: 'result', value: 'home_win' },
  { name: 'Away Win', points: 10, type: 'result', value: 'away_win' },
  { name: 'Draw', points: 15, type: 'result', value: 'draw' },
  { name: 'Home Total Goals', points: 5, type: 'goals', value: 'home_goals' },
  { name: 'Away Total Goals', points: 5, type: 'goals', value: 'away_goals' },
];

const CompetitionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // Main states
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [standings, setStandings] = useState([]);
  const [joined, setJoined] = useState(false);
  const [userPredictions, setUserPredictions] = useState({});
  const [showPredictionForm, setShowPredictionForm] = useState(false);
  const [showCreatePredictionModal, setShowCreatePredictionModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Matches state
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchSearchTerm, setMatchSearchTerm] = useState('');
  const [loadingMatches, setLoadingMatches] = useState(false);
  
  const currentUserId = getUserId();
  
  // Create prediction form
  const [predictionForm, setPredictionForm] = useState({
    home_team: '',
    away_team: '',
    fixture_id: null,
    league: '',
    venue: '',
    kickoff_time: '',
    outcomes: [...DEFAULT_OUTCOMES],
    total_winners: 1,
    win_rule: 'highest_points',
    prediction_time: '',
    is_available: true,
    requires_ticket: false,
    ticket_price: 0,
    prize_percentage: 60,
    amount_to_win: 0,
  });

  // Result form
  const [resultForm, setResultForm] = useState({
    home_score: '',
    away_score: '',
  });

  // ==================== FETCH COMPETITION DATA ====================
  const fetchCompetitionData = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, predsRes, standingsRes, joinedRes, userPredsRes] = await Promise.all([
        competitionAPI.getSingle(id),
        competitionAPI.getPredictions(id, { filter: 'all' }),
        competitionAPI.getStandings(id),
        isAuthenticated ? competitionAPI.checkJoined(id) : Promise.resolve({ data: { joined: false } }),
        isAuthenticated ? competitionAPI.getUserPredictionStatus(id) : Promise.resolve({ data: { status: {}, selections: {} } }),
      ]);

      setCompetition(compRes.data);
      
      // Enrich predictions with live/final scores
      const enrichedPredictions = (predsRes.data || []).map(pred => {
        const kickoffTime = pred.kickoff_time ? new Date(pred.kickoff_time).getTime() : 0;
        const now = Date.now();
        const hasStarted = kickoffTime > 0 && kickoffTime <= now;
        const isEnded = pred.status === 'completed' || pred.is_ended;
        
        // Determine match state
        let matchState = 'upcoming';
        if (isEnded) matchState = 'ended';
        else if (hasStarted) matchState = 'live';
        
        return {
          ...pred,
          _matchState: matchState,
          _hasStarted: hasStarted,
          _isEnded: isEnded,
          _isLive: !isEnded && hasStarted,
          _hasScore: pred.home_score !== null && 
                     pred.home_score !== undefined && 
                     pred.home_score !== '' &&
                     pred.away_score !== null &&
                     pred.away_score !== undefined &&
                     pred.away_score !== '',
        };
      });
      
      setPredictions(enrichedPredictions);
      setStandings(standingsRes.data || []);
      setJoined(joinedRes.data?.joined || false);
      setUserPredictions(userPredsRes.data?.selections || {});
    } catch (error) {
      console.error('Failed to fetch competition:', error);
      toast.error('Failed to load competition');
      navigate('/competitions');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    fetchCompetitionData();
  }, [fetchCompetitionData]);

  // ==================== AUTO-REFRESH LIVE SCORES ====================
  useEffect(() => {
    // Check if any prediction is live
    const hasLiveMatches = predictions.some(pred => pred._isLive);
    
    if (!hasLiveMatches) return;
    
    // Refresh every 30 seconds while there are live matches
    const interval = setInterval(() => {
      console.log('Auto-refreshing live match scores...');
      fetchCompetitionData();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [predictions, fetchCompetitionData]);

  // ==================== FETCH UPCOMING MATCHES ====================
  const fetchUpcomingMatches = async () => {
    setLoadingMatches(true);
    try {
      const response = await matchAPI.getUpcoming();
      console.log('Upcoming matches response:', response.data);
      
      let matches = [];
      if (Array.isArray(response.data)) {
        matches = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        matches = response.data.data;
      } else if (response.data?.fixtures && Array.isArray(response.data.fixtures)) {
        matches = response.data.fixtures;
      } else if (response.data?.matches && Array.isArray(response.data.matches)) {
        matches = response.data.matches;
      }
      
      setUpcomingMatches(matches);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
      setUpcomingMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  useEffect(() => {
    if (showCreatePredictionModal) {
      fetchUpcomingMatches();
    }
  }, [showCreatePredictionModal]);

  // ==================== TEAM LOGO HELPER ====================
  const getTeamLogo = (item, teamType = 'home') => {
    if (!item) return null;
    
    if (teamType === 'home') {
      return item.home_club_logo || 
             item.home_logo || 
             item.homeTeamLogo ||
             null;
    }
    return item.away_club_logo || 
           item.away_logo || 
           item.awayTeamLogo ||
           null;
  };

  // ==================== MATCH DUPLICATE CHECK ====================
  const isMatchAlreadyPredicted = useCallback((matchId) => {
    if (!matchId) return false;
    return predictions.some(pred => {
      if (pred.fixture_id && String(pred.fixture_id) === String(matchId)) return true;
      if (pred.match_id && String(pred.match_id) === String(matchId)) return true;
      return false;
    });
  }, [predictions]);

  const getUsedMatchIds = useCallback(() => {
    return predictions
      .map(pred => pred.fixture_id || pred.match_id)
      .filter(id => id)
      .map(id => String(id));
  }, [predictions]);

  const availableMatches = React.useMemo(() => {
    const usedIds = getUsedMatchIds();
    
    return upcomingMatches.filter(match => {
      const matchId = match.id || match.fixture_id;
      
      if (usedIds.includes(String(matchId))) return false;
      
      if (!matchSearchTerm) return true;
      const search = matchSearchTerm.toLowerCase();
      return (
        match.home_team?.toLowerCase().includes(search) ||
        match.away_team?.toLowerCase().includes(search) ||
        match.home_club_name?.toLowerCase().includes(search) ||
        match.away_club_name?.toLowerCase().includes(search) ||
        match.league?.toLowerCase().includes(search) ||
        match.competition_name?.toLowerCase().includes(search)
      );
    });
  }, [upcomingMatches, predictions, matchSearchTerm, getUsedMatchIds]);

  // ==================== CHECK IF CREATOR ====================
  const isCreator = competition && currentUserId && (
    String(competition.author_id) === String(currentUserId) ||
    String(competition.author) === String(currentUserId) ||
    String(competition.creator_id) === String(currentUserId) ||
    String(competition.post_author) === String(currentUserId)
  );

  // ==================== HANDLERS ====================
  const handleJoin = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to join');
      navigate('/login');
      return;
    }

    try {
      const response = await competitionAPI.join(id);
      if (response.data.success) {
        setJoined(true);
        toast.success('Joined competition successfully!');
        await fetchCompetitionData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to join');
    }
  };

  const handlePredictionSubmit = async (data) => {
    try {
      const response = await predictionAPI.submit({
        ...data,
        competition_id: parseInt(id),
      });
      if (response.data.success) {
        toast.success('Prediction submitted successfully!');
        setShowPredictionForm(false);
        setSelectedPrediction(null);
        await fetchCompetitionData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit prediction');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: competition?.title || 'XPunters Competition',
        text: `Join the competition: ${competition?.title}`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleSelectMatch = (match) => {
    const matchId = match.id || match.fixture_id;
    
    if (isMatchAlreadyPredicted(matchId)) {
      toast.error('A prediction for this match already exists in this competition');
      return;
    }
    
    setSelectedMatch(match);
    
    setPredictionForm(prev => ({
      ...prev,
      home_team: match.home_team || match.home_club_name || '',
      away_team: match.away_team || match.away_club_name || '',
      fixture_id: matchId,
      league: match.league || match.competition_name || '',
      venue: match.venue || '',
      kickoff_time: match.kickoff_time || '',
      prediction_time: match.kickoff_time || '',
    }));
  };

  const handleClearMatch = () => {
    setSelectedMatch(null);
    setPredictionForm(prev => ({
      ...prev,
      home_team: '',
      away_team: '',
      fixture_id: null,
      league: '',
      venue: '',
      kickoff_time: '',
      prediction_time: '',
    }));
  };

  const handleCreatePrediction = async (e) => {
    e.preventDefault();

    if (!selectedMatch) {
      toast.error('Please select a match first');
      return;
    }

    const matchId = selectedMatch.id || selectedMatch.fixture_id;
    if (isMatchAlreadyPredicted(matchId)) {
      toast.error('A prediction for this match already exists in this competition');
      return;
    }

    if (predictionForm.outcomes.length === 0) {
      toast.error('Please add at least one outcome');
      return;
    }

    for (const outcome of predictionForm.outcomes) {
      if (!outcome.name || !outcome.points) {
        toast.error('Each outcome must have a name and points');
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await predictionAPI.create({
        ...predictionForm,
        competition_id: parseInt(id),
      });
      
      if (response.data.success) {
        toast.success('Prediction created successfully!');
        setShowCreatePredictionModal(false);
        resetPredictionForm();
        await fetchCompetitionData();
      }
    } catch (error) {
      if (error.response?.data?.code === 'duplicate_prediction') {
        toast.error('A prediction for this match already exists in this competition');
      } else {
        toast.error(error.response?.data?.message || 'Failed to create prediction');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetPredictionForm = () => {
    setSelectedMatch(null);
    setMatchSearchTerm('');
    setPredictionForm({
      home_team: '',
      away_team: '',
      fixture_id: null,
      league: '',
      venue: '',
      kickoff_time: '',
      outcomes: [...DEFAULT_OUTCOMES],
      total_winners: 1,
      win_rule: 'highest_points',
      prediction_time: '',
      is_available: true,
      requires_ticket: false,
      ticket_price: 0,
      prize_percentage: 60,
      amount_to_win: 0,
    });
  };

  const handleSetResult = async () => {
    if (resultForm.home_score === '' || resultForm.away_score === '') {
      toast.error('Please enter both home and away scores');
      return;
    }

    setSubmitting(true);
    try {
      const homeScore = parseInt(resultForm.home_score);
      const awayScore = parseInt(resultForm.away_score);
      
      const calculatedResults = {
        home_score: homeScore,
        away_score: awayScore,
        home_goals: homeScore,
        away_goals: awayScore,
        home_win: homeScore > awayScore,
        away_win: awayScore > homeScore,
        draw: homeScore === awayScore,
        result: homeScore > awayScore ? 'Home Win' : awayScore > homeScore ? 'Away Win' : 'Draw',
      };

      const response = await predictionAPI.updateResult(selectedPrediction.id, calculatedResults);
      
      if (response.data.success) {
        toast.success('Result updated successfully!');
        setShowResultModal(false);
        setSelectedPrediction(null);
        setResultForm({ home_score: '', away_score: '' });
        await fetchCompetitionData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update result');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePrediction = async (predictionId) => {
    if (!window.confirm('Delete this prediction? This action cannot be undone.')) return;

    try {
      const response = await predictionAPI.delete(predictionId);
      if (response.data.success) {
        toast.success('Prediction deleted');
        await fetchCompetitionData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete prediction');
    }
  };

  // Outcomes management
  const addOutcome = () => {
    setPredictionForm(prev => ({
      ...prev,
      outcomes: [...prev.outcomes, { name: '', points: 10, type: 'custom', value: '' }],
    }));
  };

  const removeOutcome = (index) => {
    setPredictionForm(prev => ({
      ...prev,
      outcomes: prev.outcomes.filter((_, i) => i !== index),
    }));
  };

  const updateOutcome = (index, field, value) => {
    setPredictionForm(prev => {
      const newOutcomes = [...prev.outcomes];
      newOutcomes[index] = { 
        ...newOutcomes[index], 
        [field]: field === 'points' ? parseInt(value) || 0 : value 
      };
      return { ...prev, outcomes: newOutcomes };
    });
  };

  const resetToDefaults = () => {
    setPredictionForm(prev => ({
      ...prev,
      outcomes: [...DEFAULT_OUTCOMES],
    }));
    toast.success('Reset to default outcomes');
  };

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="competition-detail-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="competition-not-found">
        <h2>Competition not found</h2>
        <Link to="/competitions" className="btn btn-primary">
          <FaArrowLeft /> Back to Competitions
        </Link>
      </div>
    );
  }

  const canParticipate = isAuthenticated && joined && competition?.status === 'open' && !isCreator;
  const usedMatchCount = upcomingMatches.length - availableMatches.length;

  // ==================== RENDER ====================
  return (
    <div className="competition-detail-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="competition-detail-grid">
        <div className="detail-main">
          <div className="detail-card">
            {competition.image_url && (
              <div className="detail-image-container">
                <img src={competition.image_url} alt={competition.title} className="detail-image" />
              </div>
            )}

            <div className="detail-header">
              <div className="detail-meta">
                <span className={`competition-status ${competition.status}`}>{competition.status}</span>
                {competition.visibility === 'private' && (
                  <span className="detail-private"><FaLock /> Private</span>
                )}
                {competition.visibility === 'public' && (
                  <span className="detail-public"><FaUnlock /> Public</span>
                )}
                {competition.is_free ? (
                  <span className="free-badge">Free Entry</span>
                ) : (
                  <span className="price-badge">{formatXPUNT(competition.join_price)}</span>
                )}
              </div>
              <div className="detail-actions">
                <button onClick={handleShare} className="action-btn"><FaShare /></button>
              </div>
            </div>

            <h1 className="detail-title">{competition.title}</h1>
            {competition.description && (
              <p className="detail-description">{competition.description}</p>
            )}

            {competition.prize_distribution && competition.prize_distribution.length > 0 && (
              <div className="prize-distribution-info">
                <h4><FaTrophy /> Prize Distribution</h4>
                <div className="distribution-grid">
                  {competition.prize_distribution.map((dist, index) => (
                    <div key={index} className="distribution-item">
                      <span className="position">
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        {index > 2 && `${index + 1}th`}
                      </span>
                      <span className="percentage">{dist.percentage}%</span>
                      <span className="amount">{formatXPUNT(dist.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="detail-stats">
              <div className="stat-item">
                <FaUsers className="stat-icon" />
                <div>
                  <span className="stat-value">{competition.players_count || 0}</span>
                  <span className="stat-label">Players</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaTrophy className="stat-icon" />
                <div>
                  <span className="stat-value">{formatXPUNT(competition.total_prize_pool || 0)}</span>
                  <span className="stat-label">Prize Pool</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaClock className="stat-icon" />
                <div>
                  <span className="stat-value">{formatTimeAgo(competition.created_at)}</span>
                  <span className="stat-label">Created</span>
                </div>
              </div>
            </div>

            {!joined && competition.status === 'open' && !isCreator && (
              <div className="join-section">
                <button onClick={handleJoin} className="btn btn-primary btn-lg">
                  Join Competition {!competition.is_free && `(${formatXPUNT(competition.join_price)})`}
                </button>
              </div>
            )}

            {joined && !isCreator && (
              <div className="joined-badge"><FaCheck /> You've joined this competition</div>
            )}

            {isCreator && (
              <div className="creator-badge"><FaTrophy /> You are the creator of this competition</div>
            )}
          </div>

          {/* ==================== PREDICTIONS LIST ==================== */}
          <div className="detail-card">
            <div className="predictions-header">
              <h3>🎯 Predictions ({predictions.length})</h3>
              {isCreator && competition.status === 'open' && (
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowCreatePredictionModal(true)}
                >
                  <FaPlus /> Create Prediction
                </button>
              )}
            </div>

            {predictions.length === 0 ? (
              <div className="empty-state-small">
                <p>
                  {isCreator 
                    ? 'No predictions yet. Create the first prediction for players!' 
                    : 'No predictions available yet.'}
                </p>
                {isCreator && (
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowCreatePredictionModal(true)}
                  >
                    <FaPlus /> Create First Prediction
                  </button>
                )}
              </div>
            ) : (
              <div className="predictions-list">
                {predictions.map((pred) => {
                  const userPrediction = userPredictions[pred.id];
                  const hasSubmitted = !!userPrediction;
                  const homeLogo = getTeamLogo(pred, 'home');
                  const awayLogo = getTeamLogo(pred, 'away');
                  const isLive = pred._isLive;
                  const isEnded = pred._isEnded;
                  const hasScore = pred._hasScore;
                  
                  return (
                    <div 
                      key={pred.id} 
                      className={`prediction-item ${isLive ? 'is-live' : ''} ${isEnded ? 'is-ended' : ''}`}
                    >
                      {/* Live/Ended Badge */}
                      {isLive && (
                        <div className="match-live-badge">
                          <span className="live-dot"></span> LIVE
                        </div>
                      )}
                      {isEnded && (
                        <div className="match-ended-badge">
                          ✅ FT
                        </div>
                      )}

                      {/* Teams with logos */}
                      <div className="prediction-teams">
                        <div className="prediction-team">
                          {homeLogo ? (
                            <img 
                              src={homeLogo} 
                              alt={pred.home_team} 
                              className="team-logo-prediction"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="team-logo-placeholder small">
                              {(pred.home_team || 'H').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="team-name">{pred.home_team}</span>
                        </div>
                        
                        <span className="vs">vs</span>
                        
                        <div className="prediction-team">
                          {awayLogo ? (
                            <img 
                              src={awayLogo} 
                              alt={pred.away_team} 
                              className="team-logo-prediction"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="team-logo-placeholder small">
                              {(pred.away_team || 'A').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="team-name">{pred.away_team}</span>
                        </div>
                      </div>

                      {/* ==================== MATCH SCORE DISPLAY ==================== */}
                      {hasScore && (
                        <div className={`match-score-display ${isLive ? 'live' : ''} ${isEnded ? 'ended' : ''}`}>
                          <div className="score-box">
                            <span className="score-number">{pred.home_score}</span>
                            <span className="score-separator">-</span>
                            <span className="score-number">{pred.away_score}</span>
                          </div>
                          {isLive && (
                            <span className="score-status live">
                              <span className="live-dot"></span>
                              Live
                            </span>
                          )}
                          {isEnded && (
                            <span className="score-status ended">
                              Full Time
                            </span>
                          )}
                        </div>
                      )}

                      <div className="prediction-info">
                        <span className="prediction-status">
                          {pred.is_expired ? '⏰ Expired' : 
                           pred.status === 'completed' ? '✅ Completed' : 
                           isLive ? '🔴 Live' :
                           '⏳ Pending'}
                        </span>
                        {pred.result && (
                          <span className="prediction-result">Result: {pred.result}</span>
                        )}
                        {hasSubmitted && (
                          <span className="user-prediction-badge">
                            Your picks: {Array.isArray(userPrediction) ? userPrediction.join(', ') : userPrediction}
                          </span>
                        )}
                        {pred.requires_ticket && (
                          <span className="ticket-badge">🎫 {formatXPUNT(pred.ticket_price)}</span>
                        )}
                      </div>
                      
                      <div className="prediction-actions">
                        {canParticipate && !pred.is_expired && pred.status !== 'completed' && !hasSubmitted && !isLive && (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setSelectedPrediction(pred);
                              setShowPredictionForm(true);
                            }}
                          >
                            Predict
                          </button>
                        )}
                        
                        {hasSubmitted && !isCreator && (
                          <span className="submitted-badge"><FaCheck /> Submitted</span>
                        )}
                        
                        {isCreator && pred.status !== 'completed' && (
                          <button 
                            className="btn btn-success btn-sm"
                            onClick={() => {
                              setSelectedPrediction(pred);
                              setResultForm({
                                home_score: pred.home_score || '',
                                away_score: pred.away_score || '',
                              });
                              setShowResultModal(true);
                            }}
                          >
                            <FaCheck /> Set Result
                          </button>
                        )}
                        
                        {isCreator && pred.status === 'pending' && (
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeletePrediction(pred.id)}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          <div className="standings-card">
            <h3><FaChartBar /> Standings</h3>
            {standings.length === 0 ? (
              <div className="empty-state-small"><p>No standings yet</p></div>
            ) : (
              <div className="standings-list">
                {standings.slice(0, 10).map((entry) => (
                  <div key={entry.user_id} className="standing-item">
                    <span className="standing-rank">#{entry.rank}</span>
                    <span className="standing-name">{entry.name}</span>
                    <span className="standing-points">{entry.points} pts</span>
                    <span className="standing-winrate">{entry.win_rate}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="info-card">
            <h4>ℹ️ Competition Info</h4>
            <div className="info-item">
              <span>Total Winners</span>
              <span>{competition.total_winners || 1}</span>
            </div>
            <div className="info-item">
              <span>Prize Pool</span>
              <span className="gold">{formatXPUNT(competition.total_prize_pool || 0)}</span>
            </div>
            <div className="info-item">
              <span>Created By</span>
              <span>{competition.author}</span>
            </div>
            <div className="info-item">
              <span>Matches Used</span>
              <span>{predictions.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== PLAYER: Prediction Form Modal ==================== */}
      {showPredictionForm && selectedPrediction && (
        <PredictionForm
          prediction={selectedPrediction}
          onSubmit={handlePredictionSubmit}
          onCancel={() => {
            setShowPredictionForm(false);
            setSelectedPrediction(null);
          }}
        />
      )}

      {/* ==================== CREATOR: Create Prediction Modal ==================== */}
      {showCreatePredictionModal && (
        <div className="modal-overlay">
          <div className="modal-content create-prediction-modal">
            <div className="modal-header">
              <h2><FaPlus /> Create Prediction</h2>
              <button 
                className="close-btn" 
                onClick={() => {
                  setShowCreatePredictionModal(false);
                  resetPredictionForm();
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleCreatePrediction} className="modal-body">
              {!selectedMatch ? (
                <div className="match-selection-section">
                  <div className="section-title">
                    <FaFutbol className="section-icon" />
                    <span>Select Match from Upcoming Fixtures</span>
                  </div>

                  {predictions.length > 0 && (
                    <div className="used-matches-notice">
                      <FaInfoCircle className="info-icon" />
                      <span>
                        {predictions.length} match(es) already have predictions in this competition
                      </span>
                    </div>
                  )}

                  <div className="search-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search matches by team or league..."
                      value={matchSearchTerm}
                      onChange={(e) => setMatchSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>

                  {loadingMatches ? (
                    <div className="loading-matches">
                      <LoadingSpinner size="small" />
                      <span>Loading matches...</span>
                    </div>
                  ) : upcomingMatches.length === 0 ? (
                    <div className="empty-state-small">
                      <FaFutbol className="empty-icon" />
                      <p>No upcoming matches available</p>
                      <small>Ask admin to add matches first</small>
                    </div>
                  ) : availableMatches.length === 0 ? (
                    <div className="empty-state-small">
                      <FaCheck className="empty-icon" />
                      <p>All upcoming matches already have predictions</p>
                      <small>Each match can only have one prediction in this competition</small>
                    </div>
                  ) : (
                    <>
                      <div className="matches-selection-list">
                        {availableMatches.map((match) => {
                          const matchId = match.id || match.fixture_id;
                          const homeLogo = getTeamLogo(match, 'home');
                          const awayLogo = getTeamLogo(match, 'away');
                          
                          return (
                            <div 
                              key={matchId}
                              className="match-selection-item"
                              onClick={() => handleSelectMatch(match)}
                            >
                              <div className="match-teams-select">
                                <div className="team-with-logo">
                                  {homeLogo ? (
                                    <img 
                                      src={homeLogo} 
                                      alt={match.home_team || match.home_club_name} 
                                      className="team-logo-select"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) {
                                          e.target.nextSibling.style.display = 'flex';
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <span 
                                    className="team-logo-placeholder"
                                    style={{ display: homeLogo ? 'none' : 'flex' }}
                                  >
                                    {(match.home_team || match.home_club_name || 'H').charAt(0).toUpperCase()}
                                  </span>
                                  <span className="home-team">
                                    {match.home_team || match.home_club_name}
                                  </span>
                                </div>
                                
                                <span className="vs">vs</span>
                                
                                <div className="team-with-logo">
                                  {awayLogo ? (
                                    <img 
                                      src={awayLogo} 
                                      alt={match.away_team || match.away_club_name} 
                                      className="team-logo-select"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) {
                                          e.target.nextSibling.style.display = 'flex';
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <span 
                                    className="team-logo-placeholder"
                                    style={{ display: awayLogo ? 'none' : 'flex' }}
                                  >
                                    {(match.away_team || match.away_club_name || 'A').charAt(0).toUpperCase()}
                                  </span>
                                  <span className="away-team">
                                    {match.away_team || match.away_club_name}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="match-info-select">
                                {(match.league || match.competition_name) && (
                                  <span className="match-league">
                                    <FaGlobe /> {match.league || match.competition_name}
                                  </span>
                                )}
                                {match.kickoff_time && (
                                  <span className="match-time">
                                    <FaClock /> {formatDate(match.kickoff_time)}
                                  </span>
                                )}
                              </div>
                              
                              <button type="button" className="select-match-btn">
                                Select <FaCheck />
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      {usedMatchCount > 0 && (
                        <div className="used-matches-info">
                          <FaInfoCircle className="info-icon" />
                          <span>
                            {usedMatchCount} match(es) already have predictions and are hidden from this list
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="selected-match-info">
                    <div className="selected-match-header">
                      <span className="selected-label"><FaCheck /> Match Selected</span>
                      <button 
                        type="button"
                        className="change-match-btn"
                        onClick={handleClearMatch}
                      >
                        <FaEdit /> Change
                      </button>
                    </div>
                    <div className="selected-match-content">
                      <div className="selected-teams">
                        <div className="selected-team">
                          {getTeamLogo(selectedMatch, 'home') ? (
                            <img 
                              src={getTeamLogo(selectedMatch, 'home')} 
                              alt={selectedMatch.home_team || selectedMatch.home_club_name} 
                              className="team-logo-selected"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="team-logo-placeholder selected">
                              {(selectedMatch.home_team || selectedMatch.home_club_name || 'H').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="team">{selectedMatch.home_team || selectedMatch.home_club_name}</span>
                        </div>
                        
                        <span className="vs">vs</span>
                        
                        <div className="selected-team">
                          {getTeamLogo(selectedMatch, 'away') ? (
                            <img 
                              src={getTeamLogo(selectedMatch, 'away')} 
                              alt={selectedMatch.away_team || selectedMatch.away_club_name} 
                              className="team-logo-selected"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="team-logo-placeholder selected">
                              {(selectedMatch.away_team || selectedMatch.away_club_name || 'A').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="team">{selectedMatch.away_team || selectedMatch.away_club_name}</span>
                        </div>
                      </div>
                      
                      <div className="selected-meta">
                        {(selectedMatch.league || selectedMatch.competition_name) && (
                          <span><FaGlobe /> {selectedMatch.league || selectedMatch.competition_name}</span>
                        )}
                        {selectedMatch.kickoff_time && (
                          <span><FaClock /> {formatDate(selectedMatch.kickoff_time)}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="outcomes-header">
                      <label>
                        <FaBullseye /> Prediction Outcomes *
                      </label>
                      <button 
                        type="button"
                        className="btn btn-secondary btn-xs"
                        onClick={resetToDefaults}
                      >
                        Reset to Defaults
                      </button>
                    </div>
                    <small className="field-hint">
                      Players will select from these outcomes. Each correct outcome gives the player the points shown.
                    </small>
                    
                    <div className="outcomes-list">
                      {predictionForm.outcomes.map((outcome, index) => (
                        <div key={index} className="outcome-row">
                          <input
                            type="text"
                            placeholder="Outcome name (e.g., Home Win)"
                            value={outcome.name}
                            onChange={(e) => updateOutcome(index, 'name', e.target.value)}
                            className="outcome-name-input"
                          />
                          <div className="outcome-points-wrapper">
                            <input
                              type="number"
                              placeholder="Points"
                              value={outcome.points}
                              onChange={(e) => updateOutcome(index, 'points', e.target.value)}
                              min="1"
                              className="outcome-points-input"
                            />
                            <span className="points-label">pts</span>
                          </div>
                          <button 
                            type="button"
                            className="remove-outcome-btn"
                            onClick={() => removeOutcome(index)}
                            disabled={predictionForm.outcomes.length <= 1}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      type="button" 
                      className="btn btn-secondary btn-sm add-outcome-btn"
                      onClick={addOutcome}
                    >
                      <FaPlus /> Add Custom Outcome
                    </button>
                  </div>

                  <div className="form-group">
                    <label>Prediction Deadline</label>
                    <input
                      type="datetime-local"
                      value={predictionForm.prediction_time}
                      onChange={(e) => setPredictionForm(prev => ({ ...prev, prediction_time: e.target.value }))}
                    />
                    <small className="field-hint">Players cannot submit after this time</small>
                  </div>

                  <div className="form-group">
                    <label>Total Winners</label>
                    <input
                      type="number"
                      value={predictionForm.total_winners}
                      onChange={(e) => setPredictionForm(prev => ({ ...prev, total_winners: parseInt(e.target.value) || 1 }))}
                      min="1"
                      max="10"
                    />
                    <small className="field-hint">How many top-scoring predictions win</small>
                  </div>

                  <div className="form-info-box">
                    <FaInfoCircle className="info-icon" />
                    <p>
                      <strong>How scoring works:</strong> Players select outcomes they predict will happen. 
                      Each correct outcome awards the player the points you set. The player with the most points wins!
                    </p>
                  </div>
                </>
              )}

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreatePredictionModal(false);
                    resetPredictionForm();
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={submitting || !selectedMatch}
                >
                  {submitting ? <LoadingSpinner size="small" /> : <><FaPlus /> Create Prediction</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== CREATOR: Set Result Modal ==================== */}
      {showResultModal && selectedPrediction && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2><FaCalculator /> Enter Match Result</h2>
              <button 
                className="close-btn" 
                onClick={() => setShowResultModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="match-result-info">
                <div className="result-teams">
                  <div className="result-team">
                    {getTeamLogo(selectedPrediction, 'home') ? (
                      <img 
                        src={getTeamLogo(selectedPrediction, 'home')} 
                        alt={selectedPrediction.home_team} 
                        className="team-logo-result"
                      />
                    ) : (
                      <span className="team-logo-placeholder medium">
                        {(selectedPrediction.home_team || 'H').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="team-name">{selectedPrediction.home_team}</span>
                  </div>
                  
                  <span className="vs">vs</span>
                  
                  <div className="result-team">
                    {getTeamLogo(selectedPrediction, 'away') ? (
                      <img 
                        src={getTeamLogo(selectedPrediction, 'away')} 
                        alt={selectedPrediction.away_team} 
                        className="team-logo-result"
                      />
                    ) : (
                      <span className="team-logo-placeholder medium">
                        {(selectedPrediction.away_team || 'A').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="team-name">{selectedPrediction.away_team}</span>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{selectedPrediction.home_team} Score *</label>
                  <input
                    type="number"
                    value={resultForm.home_score}
                    onChange={(e) => setResultForm(prev => ({ ...prev, home_score: e.target.value }))}
                    min="0"
                    placeholder="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{selectedPrediction.away_team} Score *</label>
                  <input
                    type="number"
                    value={resultForm.away_score}
                    onChange={(e) => setResultForm(prev => ({ ...prev, away_score: e.target.value }))}
                    min="0"
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              {resultForm.home_score !== '' && resultForm.away_score !== '' && (
                <div className="calculated-results">
                  <h4><FaCheck /> Results that will be marked correct:</h4>
                  <div className="calculated-list">
                    {parseInt(resultForm.home_score) > parseInt(resultForm.away_score) && (
                      <span className="calculated-item success">
                        <FaCheck /> Home Win
                      </span>
                    )}
                    {parseInt(resultForm.away_score) > parseInt(resultForm.home_score) && (
                      <span className="calculated-item success">
                        <FaCheck /> Away Win
                      </span>
                    )}
                    {parseInt(resultForm.home_score) === parseInt(resultForm.away_score) && (
                      <span className="calculated-item success">
                        <FaCheck /> Draw
                      </span>
                    )}
                    <span className="calculated-item success">
                      <FaCheck /> Home Total Goals: {resultForm.home_score}
                    </span>
                    <span className="calculated-item success">
                      <FaCheck /> Away Total Goals: {resultForm.away_score}
                    </span>
                  </div>
                </div>
              )}

              <div className="form-info-box warning">
                <FaInfoCircle className="info-icon" />
                <p>Once you set the result, players who predicted correctly will earn points automatically. This cannot be undone.</p>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowResultModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleSetResult}
                  disabled={submitting || resultForm.home_score === '' || resultForm.away_score === ''}
                >
                  {submitting ? <LoadingSpinner size="small" /> : <><FaCheck /> Confirm Result</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionDetail;